<template>
  <section class="dashboard-panel" aria-labelledby="projects-panel-title">
    <header class="dashboard-panel-header">
      <div>
        <h3 id="projects-panel-title" class="dashboard-panel-title">
          {{ s__('Dashboard|Projects') }}
        </h3>
        <p class="dashboard-panel-subtitle">
          {{ s__('Dashboard|Projects where you participate') }}
        </p>
      </div>
      <div class="header-actions">
        <button
          type="button"
          class="btn-link dashboard-create-project-btn dashboard-header-create-btn"
          @click="$emit('open-create-project')"
        >
          <span class="dashboard-create-project-label">{{ s__('Dashboard|Create Project') }}</span>
          <span class="dashboard-create-project-plus" aria-hidden="true">+</span>
        </button>
      </div>
    </header>

    <div class="dashboard-panel-body">
      <div v-if="loading" class="dashboard-empty-message dashboard-inline-loading">
        {{ __('Loading…') }}
      </div>

      <div v-else-if="projects.length === 0" class="dashboard-empty-state">
        <h4>{{ s__('Dashboard|Welcome to Tracktor!') }}</h4>
        <p>
          <button type="button" class="empty-state-link-btn" @click="$emit('open-create-project')">
            {{
              s__(
                "Dashboard|Let's create your first project for private work or team collaboration."
              )
            }}
          </button>
          <br />
          <br />
          <i>{{ s__('Dashboard|You need at least one project before you can create tasks.') }}</i>
        </p>
      </div>

      <div v-else>
        <ul class="dashboard-list">
          <li v-for="project in projects" :key="project.id" class="dashboard-list-item">
            <a :href="projectUrlBuilder(project)" class="dashboard-project-link">
              <div class="dashboard-list-main">
                <div class="dashboard-project-title-row">
                  <strong class="dashboard-project-title">
                    <span v-if="project.isPersonal === true" class="dashboard-project-type-icon"
                      ><CircleUserIcon :size="16" variant="blue"
                    /></span>
                    <span v-else class="dashboard-project-type-icon"
                      ><NetworkIcon :size="16"
                    /></span>
                    {{ project.title }}
                  </strong>
                </div>
                <span v-if="project.description" class="dashboard-muted">{{
                  project.description
                }}</span>
              </div>
              <div class="dashboard-list-meta">
                <span class="dashboard-metric-pill" :title="s__('Dashboard|tasks')">
                  <span class="dashboard-metric-icon dashboard-metric-icon-tasks"
                    ><ActivityIcon
                  /></span>
                  <span>{{ project.tasksCount }}</span>
                </span>
                <span class="dashboard-metric-pill" :title="s__('Dashboard|members')">
                  <span class="dashboard-metric-icon dashboard-metric-icon-members"
                    ><CircleUserIcon variant="blue"
                  /></span>
                  <span>{{ project.membersCount }}</span>
                </span>
              </div>
            </a>
            <button
              type="button"
              class="dashboard-mini-action btn-link-secondary btn-xs"
              @click="$emit('open-create-task', project)"
            >
              {{ s__('Dashboard|+ Task') }}
            </button>
          </li>
        </ul>

        <div v-if="loadingMoreProjects" class="dashboard-inline-loading">
          {{ __('Loading…') }}
        </div>

        <div v-else-if="projectsHasNextPage" class="dashboard-inline-loading">
          <button
            type="button"
            class="btn-load-more"
            :disabled="loadingMoreProjects"
            @click="$emit('load-more')"
          >
            {{ s__('Tasks|Show more') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { __, s__ } from '@/locale'
import ActivityIcon from '../common/icons/ActivityIcon.vue'
import CircleUserIcon from '../common/icons/CircleUserIcon.vue'
import NetworkIcon from '../common/icons/NetworkIcon.vue'

defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  projects: {
    type: Array,
    default: () => [],
  },
  loadingMoreProjects: {
    type: Boolean,
    default: false,
  },
  projectsHasNextPage: {
    type: Boolean,
    default: false,
  },
  projectUrlBuilder: {
    type: Function,
    required: true,
  },
})

defineEmits(['load-more', 'open-create-task', 'open-create-project'])
</script>

<style scoped>
.empty-state-link-btn {
  appearance: none;
  border: 0;
  background: none;
  color: #2aabee;
  text-decoration: underline;
  font: inherit;
  padding: 0 2px;
  cursor: pointer;
}

.empty-state-link-btn:hover {
  background: transparent;
  color: #229ed9;
}

.empty-state-link-btn:focus,
.empty-state-link-btn:active {
  background: transparent;
  color: #229ed9;
}
</style>
